/**
 * AgentsPortalPage — the hidden /agents section shell (SuperAdmin only; the
 * route is registered in AppRouter but deliberately absent from every sidebar,
 * drawer, and pageRegistry — navigate manually).
 *
 * Layout mirrors the DocAI /ai portal: a compact left rail (Dashboard, Agents,
 * Findings, Runs, Settings) + a React Router v5 <Switch> for content. All data
 * flows through the agentsManage callable via useAgents.
 */
import React from 'react';
import { Switch, Route, NavLink, useRouteMatch, Redirect } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiGrid,
  FiCpu,
  FiAlertTriangle,
  FiActivity,
  FiSettings,
} from 'react-icons/fi';

import AgentsDashboardPage from './AgentsDashboardPage';
import AgentsListPage from './AgentsListPage';
import AgentDetailPage from './AgentDetailPage';
import AgentRunsPage from './AgentRunsPage';
import AgentRunDetailPage from './AgentRunDetailPage';
import PhishingFindingsPage from './PhishingFindingsPage';
import AgentsSettingsPage from './AgentsSettingsPage';

const NAV = [
  { to: '', exact: true, label: 'Dashboard', icon: FiGrid },
  { to: '/agents', label: 'Agents', icon: FiCpu },
  { to: '/findings', label: 'Findings', icon: FiAlertTriangle },
  { to: '/runs', label: 'Runs', icon: FiActivity },
  { to: '/settings', label: 'Settings', icon: FiSettings },
];

export default function AgentsPortalPage() {
  const { path, url } = useRouteMatch();

  const railBg = useColorModeValue('gray.50', 'gray.900');
  const railBorder = useColorModeValue('gray.200', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.200');
  const inactiveColor = useColorModeValue('gray.600', 'gray.400');
  const brandColor = useColorModeValue('gray.900', 'white');

  return (
    <Flex h="calc(100vh - 150px)" minH="600px" align="stretch" overflow="hidden">
      {/* Left rail */}
      <Box
        w="200px"
        flexShrink={0}
        bg={railBg}
        borderRightWidth="1px"
        borderColor={railBorder}
        py={4}
        px={2}
        overflowY="auto"
      >
        <HStack px={3} mb={4} spacing={2}>
          <Icon as={FiCpu} color={activeColor} boxSize={5} />
          <Text fontWeight="bold" fontSize="sm" color={brandColor}>
            Agents Portal
          </Text>
        </HStack>
        <VStack align="stretch" spacing={1}>
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={`${url}${item.to}`}
              exact={item.exact}
              style={{ textDecoration: 'none' }}
            >
              <Route path={`${path}${item.to}`} exact={item.exact}>
                {({ match }) => (
                  <HStack
                    px={3}
                    py={2}
                    borderRadius="md"
                    bg={match ? activeBg : 'transparent'}
                    color={match ? activeColor : inactiveColor}
                    _hover={{ bg: match ? activeBg : railBorder }}
                    spacing={2}
                  >
                    <Icon as={item.icon} boxSize={4} />
                    <Text fontSize="sm" fontWeight={match ? 'semibold' : 'normal'}>
                      {item.label}
                    </Text>
                  </HStack>
                )}
              </Route>
            </NavLink>
          ))}
        </VStack>
        <Text px={3} mt={6} fontSize="10px" color="gray.500">
          SuperAdmin only. Hidden section — always-on agents run on a schedule in the cloud.
        </Text>
      </Box>

      {/* Content */}
      <Box flex={1} minW={0} overflowY="auto">
        <Switch>
          <Route exact path={path} component={AgentsDashboardPage} />
          <Route exact path={`${path}/agents`} component={AgentsListPage} />
          <Route exact path={`${path}/agents/:agentId`} component={AgentDetailPage} />
          <Route exact path={`${path}/findings`} component={PhishingFindingsPage} />
          <Route exact path={`${path}/runs`} component={AgentRunsPage} />
          <Route exact path={`${path}/runs/:runId`} component={AgentRunDetailPage} />
          <Route exact path={`${path}/settings`} component={AgentsSettingsPage} />
          <Redirect to={path} />
        </Switch>
      </Box>
    </Flex>
  );
}
